/**
 * Button Web Component
 * --------------------
 * This component defines a custom HTML element <button-component>
 * that renders a styled, reusable button with optional label and icon.
 *
 * Features:
 * - Automatically displays a label (via `label` attribute).
 * - Supports an optional icon (<img> or <i>) placed before the label.
 * - Can navigate to a specified URL when clicked (`action` attribute).
 * - Dispatches native "click" events for external handling.
 * - Dynamically reacts to DOM changes (icons added/removed after render).
 *
 * Example Usage:
 *  <button-component label="Learn more" action="https://example.com">
 *    <img src="icon.svg" />
 *  </button-component>
 *
 *  <button-component label="Info">
 *    <i class="material-icons">info_outline</i>
 *  </button-component>
 */

const Button = (function () {
  // --- Define the HTML structure template for the component ---
  let comptemplate = document.createElement("template");
  comptemplate.innerHTML = `
    <button class="button-container" type="button">
      <span class="icon"></span>
      <span class="button-label"></span>
    </button>
  `;

  /**
   * Button custom element definition.
   */
  class Button extends HTMLElement {
    constructor() {
      super();
    }

    /**
     * Called when the element is inserted into the DOM.
     * Initializes structure, handles label and icon rendering,
     * sets up mutation observer, and binds click behavior.
     */
    connectedCallback() {
      // --- Clone template and attach button structure ---
      this.appendChild(comptemplate.content.cloneNode(true));

      // References to button internals
      const mainButton = this.querySelector(".button-container");
      const iconSlot = this.querySelector(".icon");
      const labelEl = this.querySelector(".button-label");

      // --- Handle label text from attribute ---
      let label = this.getAttribute("label") || "";
      const mobileLabel = this.getAttribute("mobilelabel") || "";
      const isMobile =
        navigator.userAgentData?.mobile ||
        /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          navigator.userAgent.toLowerCase(),
        );
      label = isMobile && mobileLabel ? mobileLabel : label;
      labelEl.textContent = label;

      // --- Accessibility: set aria-label for screen readers ---
      if (label) {
        mainButton.setAttribute("aria-label", label);
      }

      /**
       * Updates the icon area when icon elements are added or removed.
       * If an <img> or <i> is found inside <button-component>, it is moved into
       * the icon slot and displayed before the label.
       */
      const updateIcon = () => {
        // Check for img first, then fall back to i element (for icon fonts)
        const img = this.querySelector("img");
        const iconElement = this.querySelector("i");

        if (img) {
          iconSlot.appendChild(img);
          iconSlot.style.display = "inline-flex";
          if (!img.hasAttribute("alt")) img.setAttribute("alt", "Button Icon"); // Accessibility
          img.setAttribute("aria-hidden", "true");
        } else if (iconElement) {
          iconSlot.appendChild(iconElement);
          iconSlot.style.display = "inline-flex";
          iconElement.setAttribute("aria-hidden", "true");
        } else {
          iconSlot.style.display = "none";
        }
      };

      // --- Watch for dynamic changes (e.g., new <img> tags) ---
      const observer = new MutationObserver(updateIcon);
      observer.observe(this, { childList: true });

      // Initial icon setup
      updateIcon();

      /**
       * Handle click events.
       * - Invokes `doAction()` to perform any configured navigation.
       * - Dispatches a native "click" event for external listeners.
       */
      mainButton.addEventListener("click", () => {
        this.doAction();
        this.dispatchEvent(new Event("click"));
      });
    }

    /**
     * Executes the action associated with the button.
     * If the `action` attribute is set, navigates to the specified URL.
     */
    doAction() {
      const action = this.getAttribute("action");
      const target = this.getAttribute("target");
      if (action) {
        if (target) {
          window.open(action, target);
        } else {
          window.location.href = action;
        }
      }
    }
  }

  // --- Register the custom element ---
  customElements.define("button-component", Button);

  return Button;
})();

export { Button };
